import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { formatMoney } from '../../utils/format';
import { canAccess } from '../../utils/rbac';

// Hours per working day (standard AV working day = 7.5h, week = 37.5h)
const HOURS_PER_DAY = 7.5;

export default function ProfitabilityPage({ user }) {
  const [sows,         setSows]         = useState([]);
  const [summaries,    setSummaries]    = useState({}); // sow_id → { labour[], hardware[], labour_total, hardware_total, total_cost }
  const [expanded,     setExpanded]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [statusFilter, setStatusFilter] = useState('Active');
  const [backfilling,  setBackfilling]  = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [loadAllProgress, setLoadAllProgress] = useState(null); // null | { done, total }
  const [showUntracked, setShowUntracked] = useState(false);
  const [bulkToggling,  setBulkToggling]  = useState(false);
  const [bulkResult,    setBulkResult]    = useState(null);

  const isAdmin = user?.user_role?.split(',').map(r => r.trim()).includes('admin');
  const seesMoney = canAccess('sow:see-financials');

  const fetchSowDetail = async (sowId) => {
    const data = await api.get(`profitability/sow/${sowId}`);
    setSummaries(prev => ({ ...prev, [sowId]: data }));
    return data;
  };

  const runBackfill = async () => {
    if (!window.confirm('This will set the day rate on all existing timesheet entries that are missing one, using the current active rate card. Continue?')) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const r = await api.post('timesheets/backfill-rates', {});
      setBackfillResult(`✓ Backfilled ${r.backfilled} entries${r.skipped ? `, ${r.skipped} skipped (no matching band in active rate card)` : ''}.`);
      setSummaries({});
    } catch (e) {
      setBackfillResult('✗ Backfill failed: ' + (e.message || 'Unknown error'));
    }
    setBackfilling(false);
  };

  const runLoadAll = async () => {
    const unloaded = sows.filter(s => !summaries[s.id]);
    if (unloaded.length === 0) return;
    setLoadAllProgress({ done: 0, total: unloaded.length });
    for (let i = 0; i < unloaded.length; i++) {
      try { await fetchSowDetail(unloaded[i].id); } catch { /* skip failed */ }
      setLoadAllProgress({ done: i + 1, total: unloaded.length });
    }
    setLoadAllProgress(null);
  };

  const loadSows = (status) => {
    if (!seesMoney) return;
    setLoading(true);
    const params = status === 'All' ? {} : { status };
    api.get('sows', params)
      .then(s => { setSows(s); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadSows(statusFilter); }, [statusFilter]);

  // When SOW list changes, clear stale summaries
  useEffect(() => { setSummaries({}); }, [statusFilter]);

  const toggleExpand = async (sowId) => {
    if (expanded === sowId) { setExpanded(null); return; }
    setExpanded(sowId);
    if (!summaries[sowId]) {
      try { await fetchSowDetail(sowId); }
      catch { setSummaries(prev => ({ ...prev, [sowId]: { labour: [], hardware: [], labour_total: 0, hardware_total: 0, total_cost: 0 } })); }
    }
  };

  const getCost  = (sowId) => summaries[sowId]?.total_cost    ?? null;
  const getHours = (sowId) => summaries[sowId]?.labour?.reduce((s, r) => s + (r.total_hours || 0), 0) ?? null;

  const toggleTrack = async (sow, newVal) => {
    await api.put(`sows/${sow.id}`, { track_profitability: newVal });
    setSows(prev => prev.map(s => s.id === sow.id ? { ...s, track_profitability: newVal } : s));
  };

  const bulkOptOut = async () => {
    const ids = sows.filter(s => s.track_profitability !== 0).map(s => s.id);
    if (!ids.length) { setBulkResult('All SOWs are already opted out.'); return; }
    if (!window.confirm(`Opt ${ids.length} SOW(s) out of profitability tracking. This can be reversed per-SOW at any time. Continue?`)) return;
    setBulkToggling(true);
    setBulkResult(null);
    try {
      const r = await api.post('sows/bulk-track', { ids, track: 0 });
      setBulkResult(`✓ ${r.updated} SOWs opted out of profitability tracking.`);
      setSows(prev => prev.map(s => ids.includes(s.id) ? { ...s, track_profitability: 0 } : s));
    } catch {
      setBulkResult('✗ Bulk opt-out failed — please try again.');
    }
    setBulkToggling(false);
  };

  const allProfRows = sows.map(s => {
    const revenue = s.total_excl_vat || 0;
    const cost    = getCost(s.id);
    const hours   = getHours(s.id);
    const profit  = cost !== null ? revenue - cost : null;
    const margin  = profit !== null && revenue > 0 ? (profit / revenue * 100) : null;
    const days    = hours !== null ? hours / HOURS_PER_DAY : null;
    return { sow: s, revenue, cost, hours, days, profit, margin };
  });
  const profRows   = showUntracked ? allProfRows : allProfRows.filter(p => p.sow.track_profitability !== 0);
  const trackedRows = allProfRows.filter(p => p.sow.track_profitability !== 0);
  const untrackedCount = allProfRows.length - trackedRows.length;

  const loaded    = trackedRows.filter(p => p.cost !== null);
  const totRev    = loaded.reduce((s, p) => s + p.revenue, 0);
  const totCost   = loaded.reduce((s, p) => s + p.cost,    0);
  const totLabour = loaded.reduce((s, p) => s + (summaries[p.sow.id]?.labour_total   || 0), 0);
  const totHW     = loaded.reduce((s, p) => s + (summaries[p.sow.id]?.hardware_total || 0), 0);
  const totProfit = totRev - totCost;
  const avgMargin = totRev > 0 ? (totProfit / totRev * 100) : 0;

  const STATUSES = ['Active', 'Awaiting Payment', 'Completed', 'All'];
  const allLoaded = sows.length > 0 && sows.every(s => summaries[s.id] !== undefined);

  // Profitability is SOW values and nothing else, and the API sends those only to
  // holders of sow:see-financials (pen test AD26341, AD12/AD16). Without it the
  // page says where the figures come from rather than showing every SOW at £0.
  if (!seesMoney) return (
    <div className="fade-in bg-white border border-gray-200 rounded-lg p-4 text-[10px] text-gray-600">
      These figures are SOW values, which come with the "See SOW values, rates, costs and contractor pay" access. An administrator can grant it in Admin → Access.
    </div>
  );

  return (
    <div className="fade-in">

      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="text-base font-extrabold text-navy">Profitability</h2>
          <div className="text-[9px] text-gray-500">Labour (timesheets) + Hardware (SOW hardware list) vs SOW revenue</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Load All button */}
          {!allLoaded && (
            <button
              onClick={runLoadAll}
              disabled={!!loadAllProgress}
              className="text-[9px] font-bold px-2 py-1 rounded border bg-white border-navy/40 text-navy hover:bg-navy/5 disabled:opacity-50 transition-colors"
              title="Load cost data for all SOWs at once — useful for a full audit"
            >
              {loadAllProgress
                ? `Loading ${loadAllProgress.done}/${loadAllProgress.total}…`
                : '⬇ Load All SOWs'}
            </button>
          )}
          {allLoaded && sows.length > 0 && (
            <span className="text-[8px] text-emerald-600 font-semibold">✓ All {sows.length} SOWs loaded</span>
          )}
          {isAdmin && (
            <button
              onClick={runBackfill}
              disabled={backfilling}
              className="text-[9px] font-bold px-2 py-1 rounded border bg-white border-amber-400 text-amber-700 hover:bg-amber-50 disabled:opacity-50 transition-colors"
              title="Set missing day rates on existing entries from the active rate card"
            >
              {backfilling ? 'Backfilling…' : '⚡ Fix missing rates'}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={bulkOptOut}
              disabled={bulkToggling}
              className="text-[9px] font-bold px-2 py-1 rounded border bg-white border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
              title="Mark all currently visible SOWs as 'do not track' for profitability — useful for legacy SOWs where time has not been booked"
            >
              {bulkToggling ? 'Opting out…' : '🚫 Opt out all visible'}
            </button>
          )}
          {untrackedCount > 0 && (
            <button
              onClick={() => setShowUntracked(v => !v)}
              className={`text-[9px] font-bold px-2 py-1 rounded border transition-colors ${showUntracked ? 'bg-gray-200 text-gray-700 border-gray-300' : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50'}`}
            >
              {showUntracked ? `Hide ${untrackedCount} untracked` : `Show ${untrackedCount} untracked`}
            </button>
          )}
          <div className="flex gap-1">
            {STATUSES.map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`text-[9px] font-bold px-2 py-1 rounded border transition-colors ${statusFilter === s ? 'bg-navy text-white border-navy' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Backfill result */}
      {backfillResult && (
        <div className={`mb-3 px-3 py-2 rounded text-[9px] font-medium ${backfillResult.startsWith('✓') ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {backfillResult}
        </div>
      )}
      {bulkResult && (
        <div className={`mb-3 px-3 py-2 rounded text-[9px] font-medium ${bulkResult.startsWith('✓') ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {bulkResult}
        </div>
      )}

      {/* Summary cards */}
      {loaded.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-3">
          <SummaryCard label="Revenue"      value={formatMoney(totRev)}             color="text-navy" />
          <SummaryCard label="Labour Cost"  value={formatMoney(totLabour)}          color="text-red-400" />
          <SummaryCard label="Hardware Cost" value={formatMoney(totHW)}             color="text-orange-500" />
          <SummaryCard label="Total Cost"   value={formatMoney(totCost)}            color="text-red-600" />
          <SummaryCard label="Gross Profit" value={formatMoney(totProfit)}          color={totProfit >= 0 ? 'text-emerald-600' : 'text-red-600'} />
          <SummaryCard label="Avg Margin"   value={`${avgMargin.toFixed(1)}%`}      color={avgMargin >= 20 ? 'text-emerald-600' : 'text-red-600'} />
        </div>
      )}

      {/* No-data hint */}
      {!loading && sows.length > 0 && loaded.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-3 text-[10px] text-amber-700">
          Click a SOW row to load its cost data, or use <strong>Load All SOWs</strong> for a full audit view.
        </div>
      )}

      {/* Main table */}
      {loading ? (
        <div className="text-center py-10 text-[10px] text-gray-400">Loading…</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full min-w-[680px]">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">SOW</th>
                <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Revenue</th>
                <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Hours</th>
                <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Labour</th>
                <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Hardware</th>
                <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Total Cost</th>
                <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Profit</th>
                <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Margin</th>
              </tr>
            </thead>
            <tbody>
              {profRows.length === 0 && (
                <tr><td colSpan="8" className="text-center py-8 text-[10px] text-gray-400">No SOWs found</td></tr>
              )}
              {profRows.map(p => {
                const isExp     = expanded === p.sow.id;
                const det       = summaries[p.sow.id];
                const labCost   = det?.labour_total   ?? null;
                const hwCost    = det?.hardware_total ?? null;
                const isTracked = p.sow.track_profitability !== 0;
                return (
                  <>
                    <tr
                      key={p.sow.id}
                      className={`border-b border-gray-100 last:border-0 transition-colors ${isTracked ? 'hover:bg-gold/5 cursor-pointer' : 'bg-gray-50/60 opacity-60'}`}
                      onClick={() => isTracked && toggleExpand(p.sow.id)}
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          {isTracked && <span className={`text-[8px] transition-transform ${isExp ? 'rotate-90' : ''} inline-block`}>▶</span>}
                          <div>
                            <div className="text-[10px] font-bold text-navy">{p.sow.reference}</div>
                            <div className="text-[8px] text-gray-400">{p.sow.detail}</div>
                          </div>
                          <StatusBadge status={p.sow.status} />
                          {!isTracked && <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 uppercase">Not tracked</span>}
                          <button
                            className={`ml-auto text-[7px] font-bold px-1.5 py-0.5 rounded border transition-colors ${isTracked ? 'border-gray-200 text-gray-400 hover:border-red-300 hover:text-red-500' : 'border-emerald-300 text-emerald-600 hover:bg-emerald-50'}`}
                            title={isTracked ? 'Opt this SOW out of profitability tracking' : 'Re-enable profitability tracking for this SOW'}
                            onClick={e => { e.stopPropagation(); toggleTrack(p.sow, isTracked ? 0 : 1); }}
                          >
                            {isTracked ? '🚫 Opt out' : '✓ Track'}
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-[10px] text-right">{formatMoney(p.revenue)}</td>
                      <td className="px-3 py-2 text-[10px] text-right text-gray-500">
                        {p.hours !== null ? `${p.hours.toFixed(1)}h` : <LoadHint />}
                      </td>
                      <td className="px-3 py-2 text-[10px] text-right text-red-400 font-semibold">
                        {labCost !== null ? formatMoney(labCost) : <LoadHint />}
                      </td>
                      <td className="px-3 py-2 text-[10px] text-right text-orange-500 font-semibold">
                        {hwCost !== null ? (hwCost > 0 ? formatMoney(hwCost) : <span className="text-gray-300">—</span>) : <LoadHint />}
                      </td>
                      <td className="px-3 py-2 text-[10px] text-right font-bold">
                        {p.cost !== null ? formatMoney(p.cost) : <LoadHint />}
                      </td>
                      <td className={`px-3 py-2 text-[10px] text-right font-bold ${p.profit !== null ? (p.profit >= 0 ? 'text-emerald-600' : 'text-red-600') : ''}`}>
                        {p.profit !== null ? formatMoney(p.profit) : <LoadHint />}
                      </td>
                      <td className={`px-3 py-2 text-[10px] text-right font-bold ${p.margin !== null ? (p.margin >= 20 ? 'text-emerald-600' : 'text-red-600') : ''}`}>
                        {p.margin !== null ? `${p.margin.toFixed(1)}%` : <LoadHint />}
                      </td>
                    </tr>

                    {/* Expanded breakdown */}
                    {isExp && (
                      <tr key={`${p.sow.id}-detail`} className="bg-gray-50/80">
                        <td colSpan="8" className="px-6 py-4 border-b border-gray-100">
                          {!det ? (
                            <div className="text-[9px] text-gray-400">Loading breakdown…</div>
                          ) : (
                            <div className="flex gap-8 flex-wrap">

                              {/* Labour section */}
                              <div className="flex-1 min-w-[280px]">
                                <div className="text-[8px] font-bold uppercase text-red-400 mb-1.5 flex items-center gap-1">
                                  <span>👤 Labour</span>
                                  <span className="text-gray-400 font-normal">— timesheet hours × day rate</span>
                                </div>
                                {det.labour.length === 0 ? (
                                  <div className="text-[9px] text-gray-400 italic">No timesheet entries yet.</div>
                                ) : (
                                  <table className="w-full">
                                    <thead>
                                      <tr>
                                        <th className="text-left text-[7px] uppercase text-gray-400 font-bold pb-1">Person</th>
                                        <th className="text-left text-[7px] uppercase text-gray-400 font-bold pb-1">Band</th>
                                        <th className="text-right text-[7px] uppercase text-gray-400 font-bold pb-1">Rate/day</th>
                                        <th className="text-right text-[7px] uppercase text-gray-400 font-bold pb-1">Hours</th>
                                        <th className="text-right text-[7px] uppercase text-gray-400 font-bold pb-1">Cost</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {det.labour.map((r, i) => (
                                        <tr key={i} className="border-t border-gray-100">
                                          <td className="py-1 text-[9px] font-semibold text-navy pr-3">{r.person_name}</td>
                                          <td className="py-1 text-[9px] text-gray-500 pr-3">{r.resource_type || '—'}</td>
                                          <td className="py-1 text-[9px] text-right pr-3 text-gray-500">
                                            {r.avg_day_rate ? formatMoney(r.avg_day_rate, 2) : <span className="text-amber-500 text-[8px]">no rate</span>}
                                          </td>
                                          <td className="py-1 text-[9px] text-right pr-3">{r.total_hours.toFixed(1)}h</td>
                                          <td className="py-1 text-[9px] font-semibold text-right text-red-500">
                                            {r.avg_day_rate
                                              ? formatMoney(r.labour_cost || 0)
                                              : <span className="text-amber-600">{formatMoney(r.labour_cost || 0)} ⚠</span>}
                                          </td>
                                        </tr>
                                      ))}
                                      <tr className="border-t-2 border-red-100">
                                        <td colSpan="4" className="pt-1 text-[8px] font-bold text-red-400 uppercase">Labour total</td>
                                        <td className="pt-1 text-[9px] font-bold text-right text-red-500">{formatMoney(det.labour_total)}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                )}
                              </div>

                              {/* Hardware section */}
                              <div className="flex-1 min-w-[320px]">
                                <div className="text-[8px] font-bold uppercase text-orange-500 mb-1.5 flex items-center gap-1">
                                  <span>📦 Hardware</span>
                                  <span className="text-gray-400 font-normal">— SOW hardware list (cost vs sell)</span>
                                </div>
                                {det.hardware.length === 0 ? (
                                  <div className="text-[9px] text-gray-400 italic">No hardware lines on this SOW.</div>
                                ) : (
                                  <table className="w-full">
                                    <thead>
                                      <tr>
                                        <th className="text-left text-[7px] uppercase text-gray-400 font-bold pb-1">Item</th>
                                        <th className="text-right text-[7px] uppercase text-gray-400 font-bold pb-1">Qty</th>
                                        <th className="text-right text-[7px] uppercase text-gray-400 font-bold pb-1">Cost ea.</th>
                                        <th className="text-right text-[7px] uppercase text-gray-400 font-bold pb-1">Sell ea.</th>
                                        <th className="text-right text-[7px] uppercase text-gray-400 font-bold pb-1">Cost</th>
                                        <th className="text-right text-[7px] uppercase text-gray-400 font-bold pb-1">Sell</th>
                                        <th className="text-right text-[7px] uppercase text-gray-400 font-bold pb-1">Margin</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {det.hardware.map((h, i) => {
                                        const margin = h.margin_pct != null
                                          ? h.margin_pct
                                          : (h.line_sell > 0 ? ((h.line_sell - h.line_cost) / h.line_sell * 100) : null);
                                        return (
                                          <tr key={i} className="border-t border-gray-100">
                                            <td className="py-1 text-[9px] text-navy pr-2 max-w-[160px] truncate" title={h.item_description}>
                                              {h.item_description}
                                              {h.manufacturer && <span className="block text-[7px] text-gray-400">{h.manufacturer}</span>}
                                            </td>
                                            <td className="py-1 text-[9px] text-right pr-2">{h.quantity}</td>
                                            <td className="py-1 text-[9px] text-right pr-2 text-orange-500">{formatMoney(h.unit_cost, 2)}</td>
                                            <td className="py-1 text-[9px] text-right pr-2 text-emerald-600">{formatMoney(h.unit_sell, 2)}</td>
                                            <td className="py-1 text-[9px] text-right pr-2 font-semibold text-orange-500">{formatMoney(h.line_cost)}</td>
                                            <td className="py-1 text-[9px] text-right pr-2 font-semibold text-emerald-600">{formatMoney(h.line_sell)}</td>
                                            <td className={`py-1 text-[9px] text-right font-semibold ${margin != null ? (margin >= 20 ? 'text-emerald-600' : 'text-amber-600') : 'text-gray-300'}`}>
                                              {margin != null ? `${margin.toFixed(1)}%` : '—'}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                      <tr className="border-t-2 border-orange-100">
                                        <td colSpan="4" className="pt-1 text-[8px] font-bold text-orange-500 uppercase">Hardware total (cost)</td>
                                        <td className="pt-1 text-[9px] font-bold text-right text-orange-500">{formatMoney(det.hardware_total)}</td>
                                        <td className="pt-1 text-[9px] font-bold text-right text-emerald-600">
                                          {formatMoney(det.hardware.reduce((s, h) => s + (h.line_sell || 0), 0))}
                                        </td>
                                        <td />
                                      </tr>
                                    </tbody>
                                  </table>
                                )}
                              </div>

                            </div>
                          )}

                          {/* Combined total row */}
                          {det && (
                            <div className="mt-3 pt-2 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2">
                              <div className="flex gap-4 text-[9px]">
                                <span className="text-gray-500">Revenue: <span className="font-bold text-navy">{formatMoney(p.revenue)}</span></span>
                                <span className="text-gray-500">Labour: <span className="font-bold text-red-500">{formatMoney(det.labour_total)}</span></span>
                                <span className="text-gray-500">Hardware: <span className="font-bold text-orange-500">{formatMoney(det.hardware_total)}</span></span>
                                <span className="text-gray-500">Total Cost: <span className="font-bold">{formatMoney(det.total_cost)}</span></span>
                              </div>
                              <div className={`text-[10px] font-extrabold ${p.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {p.profit >= 0 ? '▲' : '▼'} {formatMoney(Math.abs(p.profit || 0))} {p.margin !== null ? `(${p.margin.toFixed(1)}%)` : ''}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="mt-3 text-[8px] text-gray-400">
        Labour cost = Σ (hours × day rate ÷ {HOURS_PER_DAY}). Day rates snapshotted at booking time; use ⚡ Fix missing rates if any show £0.
        Hardware cost = Σ line cost from the SOW's hardware list (what we pay). Sell column shows what was charged to the client.
        Use <strong>Load All SOWs</strong> to pre-load every SOW for a complete audit view.
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function SummaryCard({ label, value, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded p-2">
      <div className="text-[8px] text-gray-500 uppercase font-bold">{label}</div>
      <div className={`text-base font-extrabold ${color}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    Active:    'bg-emerald-100 text-emerald-700',
    Draft:     'bg-gray-100 text-gray-500',
    Completed: 'bg-blue-100 text-blue-700',
    Cancelled: 'bg-red-100 text-red-500',
  };
  return (
    <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded uppercase ${colors[status] || 'bg-gray-100 text-gray-400'}`}>
      {status}
    </span>
  );
}

function LoadHint() {
  return <span className="text-[8px] text-gray-300 italic">click to load</span>;
}
